<template>
  <div :style="`width: ${width}`" class="p-chart">
    <div class="p-chart-wrapper">
      <div class="p-chart-header">
        <h4 class="p-chart-title">{{ title }}</h4>
        <div class="p-chart-filter" v-if="filterType === 'span'">
          <template v-for="(filter,key) in dataFilters">
            <span
              @click="makeActive(filter, key)"
              ref="spanFilter"
              class="filterList"
              :key="key"
            >{{filter}}</span>
          </template>
        </div>
        <div class="p-chart-filter" v-else>
          <span class="p-chart-filter-value" @click="toggleFilterDisplay">
            {{ activeFilter || 'FIlter' }}
            <img
              src="../../../assets/icons/chev_down.png"
              alt="Arrowdown icon"
            />
          </span>
          <ul class="p-chart-filter-items" v-if="showFilterItems">
            <li
              @click="handleFilterClick(filter)"
              v-for="filter in dataFilters"
              :key="filter"
            >{{ filter }}</li>
          </ul>
        </div>
      </div>
      <div class="total-value" :class="alignTotalValue" v-if="alignCurrency === 'left'">
        <span class="currency">{{ currency }}</span>
        <span class="value" style="padding-left: 10px;">{{ totalValue | formatAmount }}</span>
      </div>
      <div class="total-value" :class="alignTotalValue" v-else>
        <span class="value">{{ totalValue | formatAmount }}</span>
        <span class="currency" style="padding-left: 10px;">{{ currency }}</span>
      </div>
      <div class="percentageDiff" v-if="percentageDiff">
        <span>{{`Percentage: ${percentageDiff}%`}}</span>
      </div>
      <canvas ref="lineGraphCanvas"></canvas>
    </div>
  </div>
</template>

<script>
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable operator-linebreak */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-plusplus */
import theme from '@/mixins/theme';
import Chart from 'chart.js';

export default {
  mixins: [theme],
  name: 'LineGraph',
  /**
   * props to be passed
   * @title title of the graph
   * @width width of the graph
   * @totalValue sum total of the values
   * @currency currency to use
   * @graphData graph data to render
   * @label labels to render by
   * @dataFilter filter by period
   * @colors as appropraite
   */
  props: {
    title: {
      type: String,
      required: true,
    },
    width: {
      type: String,
      default: '60%',
    },
    totalValue: {
      type: Number,
      default: 0,
    },
    currency: {
      type: String,
      default: 'NGN',
    },
    graphData: {
      type: Array,
      required: true,
      default: () => [],
    },
    labels: {
      type: Array,
      required: true,
      default: () => [],
    },
    dataFilters: {
      type: Array,
      required: true,
      default: () => ['Last Week', 'This Week', 'Last Month', 'Custom Period'],
    },
    gradientColor: {
      type: String,
      default: '#0267ff0f',
    },
    primaryColor: {
      type: String,
      default: '#0267FF',
    },
    tooltipColor: {
      type: String,
      default: 'black',
    },
    tooltipFontColor: {
      type: String,
      default: '#F9FAFC',
    },
    ticksFontColor: {
      type: String,
      default: '#9DA8B6',
    },
    fontFamily: {
      type: String,
      default: 'Graphik',
    },
    // align the total value section to the left,center or right
    alignTotalValue: {
      type: String,
      default: 'alignTotalValueLeft',
    },
    // align the currency to the left or right of the value
    alignCurrency: {
      type: String,
      default: 'left',
    },
    // show percentage change
    percentageDiff: {
      type: Number,
    },
    filterType: {
      type: String,
      default: 'span',
    },
    filterSelected: {
      type: String,
    },
  },
  data() {
    return {
      showFilterItems: false,
      activeFilter: this.filterSelected, // Assign to a prop
      activeClass: '',
    };
  },
  filters: {
    /**
     * Formats amount without currency
     * @param amount
     * */
    formatAmount(amount) {
      const formatter = Intl.NumberFormat('en-US');
      return formatter.format(Number(amount));
    },
  },
  mounted() {
    this.mountChart();
    const filterPos = this.checkFIlterPosition(
      this.filterSelected,
      this.dataFilters,
    );
    if (this.$refs.spanFilter) {
      for (let i = 0; i < this.$refs.spanFilter.length; i++) {
        if (filterPos === i) {
          this.$refs.spanFilter[i].style = 'color: #0050AF';
        } else {
          this.$refs.spanFilter[i].style = 'color: #9DA8B6';
        }
      }
    }
  },
  updated() {
    this.mountChart();
  },
  methods: {
    mountChart() {
      const self = this;
      const canvas = this.$refs.lineGraphCanvas;
      // eslint-disable-next-line no-new
      new Chart(canvas, {
        type: 'line',
        plugins: [
          // For gradient responsiveness
          {
            id: 'responsiveGradient',
            afterLayout(chart) {
              const { scales } = chart;

              // create a linear gradient with the dimensions of the scale
              const color = chart.ctx.createLinearGradient(
                scales['x-axis-0'].right,
                scales['y-axis-0'].bottom,
                scales['x-axis-0'].left,
                scales['y-axis-0'].top,
              );
              color.addColorStop(0, `${self.gradientColor}`);
              color.addColorStop(1, 'rgba(255,225,255,0)');
              // changes the background color option
              // eslint-disable-next-line no-param-reassign
              chart.data.datasets[0].backgroundColor = color;
            },
          },
        ],
        data: {
          labels: self.labels,
          datasets: [
            {
              borderWidth: 2,
              borderColor: self.primaryColor,
              data: self.graphData,
              hoverBackgroundColor: '#FFF',
              hoverBorderColor: self.primaryColor,
              hoverBorderWidth: 6,
            },
          ],
        },
        maintainAspectRatio: false,
        responsive: true,
        options: {
          legend: {
            display: false,
          },
          tooltips: {
            mode: 'nearest',
            intersect: false,
            callbacks: {
              title() {
                return '';
              },
              label(tooltipItem, data) {
                return `${self.currency} ${
                  data.datasets[0].data[tooltipItem.index]
                }`;
              },
            },
            backgroundColor: self.tooltipColor,
            titleFontSize: 16,
            bodyFontColor: self.tooltipFontColor,
            bodyFontSize: 14,
            displayColors: false,
            xPadding: 10,
            yPadding: 10,
            cornerRadius: 4,
            caretPadding: 6,
          },
          elements: {
            point: {
              hoverRadius: 4,
              hoverBorderWidth: 1,
              backgroundColor: 'red',
              borderColor: 'red',
              radius: 0,
            },
            line: {
              borderWidth: 0.1,
            },
          },
          scales: {
            xAxes: [
              {
                type: 'category',
                ticks: {
                  fontColor: self.ticksFontColor,
                  fontSize: '12',
                  fontFamily: self.fontFamily,
                },
                gridLines: {
                  tickMarkLength: 19,
                  drawBorder: false,
                  display: false,
                },
              },
            ],
            yAxes: [
              {
                ticks: {
                  fontColor: self.ticksFontColor,
                  fontSize: '12',
                  fontFamily: self.fontFamily,
                  padding: 18,
                  beginAtZero: true,
                },
                gridLines: {
                  color: '#EFF2F7',
                  zeroLineColor: '#EFF2F7',
                  drawBorder: false,
                  // display: false
                },
              },
            ],
          },
        },
      });
    },

    toggleFilterDisplay() {
      this.showFilterItems = !this.showFilterItems;
    },

    handleFilterClick(filter) {
      this.toggleFilterDisplay();
      this.activeFilter = filter;
      this.$emit('filterUpdate', filter);
    },
    makeActive(filter, id) {
      for (let i = 0; i < this.$refs.spanFilter.length; i++) {
        if (id === i) {
          this.$refs.spanFilter[i].style = 'color: #0050AF';
        } else {
          this.$refs.spanFilter[i].style = 'color: #9DA8B6';
        }
      }
      this.$emit('filterUpdate', filter);
    },
    checkFIlterPosition(filter, filters) {
      return filters.indexOf(filter);
    },
  },
};
</script>

<style scoped lang="scss">
@import 'line-graph';
</style>
